import { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAssociations } from 'src/shared/services/associationService';
import { Association } from 'src/shared/models/association/association';
import { Associations } from 'src/modules/invoice/models/invoiceTemplate';

type InvoiceTemplateAddAssociationsProps = {
  // Redan valda föreningar visas inte som alternativ.
  selected: Associations[];
  callback: (added: Associations[]) => void;
  closeEdit: () => void;
};

export const InvoiceTemplateAddAssociations = ({ selected, callback, closeEdit }: InvoiceTemplateAddAssociationsProps) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(false);
  const [associations, setAssociations] = useState<Association[]>([]);
  const [picked, setPicked] = useState<Association[]>([]);

  useEffect(() => {
    const fetchAssociations = async () => {
      setLoading(true);
      try {
        const res = await getAssociations();
        if (isMounted()) setAssociations(res.data.results || []);
      } catch (error) {
        if (isMounted()) ConsoleHelper.log(error);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    fetchAssociations();
  }, [isMounted]);

  const options = associations.filter((association) => !selected.some((s) => s.id === association.localAss_id));

  return (
    <Box sx={{ mt: 2 }}>
      <Autocomplete
        multiple
        loading={loading}
        options={options}
        value={picked}
        onChange={(_e, value) => setPicked(value)}
        getOptionLabel={(option) => option.localAss}
        isOptionEqualToValue={(option, value) => option.localAss_id === value.localAss_id}
        renderInput={(params) => (
          <TextField {...params} size='small' label={t('invoice:adminInvoiceTemplatePage.templateInformation.notifyAssociations')} />
        )}
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant='outlined' size='small' onClick={closeEdit}>
          {t('common:button.cancel')}
        </Button>
        <Button
          variant='contained'
          disableElevation
          size='small'
          sx={{ ml: 1 }}
          disabled={!picked.length}
          onClick={() => callback(picked.map((association) => ({ id: association.localAss_id, name: association.localAss })))}
        >
          {t('common:button.choose')}
        </Button>
      </Box>
    </Box>
  );
};
